import React, { useState, useRef, forwardRef } from "react";

import ShIcon from "../../../shared/components/sh-icon/sh-icon";
import { IconPack } from "../../../shared/models/app-data-model";
import "./text-input-container.scss";

export type TextInputProps = {
  id?: string;
  ref?: React.Ref<HTMLInputElement>;
  label?: string;
  iconPack?: IconPack[];
  showIcon?: boolean;
  isRequired?: boolean;
  isDisabled?: boolean;
  type?: string;
  errorMessage?: string;
  maxLength?: number;
  formControl: { value: string; valid: boolean };
  onChangeFunction: (event: HTMLInputElement) => void;
  onClickFunction?: (event: any) => void;
  onKeyDownFunction?: (event: any) => void;
  onClearInputFunction?: (event: any) => void;
  onClearButtonBlur?: (event: React.FocusEvent<HTMLButtonElement>) => void;
  onFocusFunction?: (event: any) => void;
  inputStyle?: React.CSSProperties;
  /**
   * Optionally set the cursor position in the input. If provided, the input will move the cursor to this position on value change.
   */
  cursorPosition?: number;
};

const TextInputContainer = forwardRef<HTMLInputElement, TextInputProps>((props, ref) => {
  const {
    label,
    id,
    iconPack,
    showIcon,
    isRequired,
    isDisabled,
    type,
    errorMessage,
    maxLength,
    formControl,
    onChangeFunction,
    onClickFunction,
    onKeyDownFunction,
    onClearInputFunction,
    onClearButtonBlur,
    onFocusFunction,
    inputStyle,
    cursorPosition
  } = props;

  const [userHasFocused, setUserHasFocused] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const textInputStyles = `text${!formControl.valid ? " error" : ""}${isDisabled ? " disabled" : ""}${showIcon ? " with-icon" : ""}${userHasFocused && !formControl.value ? " user-has-focused" : ""}`;

  // Set cursor position if prop is provided and input is mounted
  React.useEffect(() => {
    if (
      typeof cursorPosition === 'number' &&
      inputRef.current
    ) {
      inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
    }
  }, [cursorPosition, formControl.value]);

  return (
    <div className="text-input-wrapper">
      <div className="text-input-container">
        <input
          ref={ref}
          data-testid={"text-input-test-id"}
          id={id}
          type="text"
          className={textInputStyles}
          style={inputStyle}
          value={formControl.value || ""}
          autoComplete="off"
          placeholder={label}
          onChange={(event) => onChangeFunction(event.target)}
          pattern={type === "number" ? "[0-9]+" : undefined}
          maxLength={maxLength ? maxLength : undefined}
          onClick={onClickFunction ? (event) => { setUserHasFocused(true); onClickFunction(event); } : () => setUserHasFocused(true)}
          onBlur={(event) => setUserHasFocused(false)}
          onKeyDown={onKeyDownFunction ? (event) => onKeyDownFunction(event) : undefined}
          onFocus={onFocusFunction ? (event) => onFocusFunction(event) : undefined}
        />
        {showIcon && (
          <button
            onClick={onClearInputFunction}
            onBlur={onClearButtonBlur}
            onMouseDown={e => {
              // Prevent button from taking focus, and refocus input
              e.preventDefault();
              if (ref && typeof ref !== 'function' && ref.current) ref.current.focus();
            }}
            className="unset-btn unstyled-btn"
            data-testid="unset-btn"
          >
            <ShIcon iconPack={iconPack} iconName="close" className="icon unset-icon" />
          </button>
        )}
      </div>
    </div>
  );
});

TextInputContainer.defaultProps = {
  id: "0",
  isRequired: false,
  type: "text",
  maxLength: undefined,
  formControl: { value: "", valid: true }
};

export default TextInputContainer;
